import type { ProtocolStateMachineSpec } from '../model.js';

export const protocolGuidedTargets = {
  connect: 'protocol.transition.connect',
  retry: 'protocol.transition.retry',
  backoff: 'protocol.state.backoff',
  failed: 'protocol.state.failed',
} as const;

/** A retrying connection: the timing facts turn a state graph into a teaching figure. */
export function protocolStateMachineFixture(width = 640, stress = false, focus: ProtocolStateMachineSpec['focus'] = 'overview'): ProtocolStateMachineSpec {
  return {
    id: stress ? 'connection-recovery-stress' : 'connection-recovery',
    family: 'protocol-state-machine', view: 'schematic',
    viewport: { width, height: width <= 390 ? 410 : 380 },
    states: stress
      ? [
          { id: 'idle', label: 'Idle', detail: 'no socket', kind: 'initial' }, { id: 'dialing', label: 'Dialing', detail: '500 ms budget' }, { id: 'auth', label: 'Authenticating', detail: 'token required' },
          { id: 'ready', label: 'Ready', detail: 'requests allowed', kind: 'success' }, { id: 'backoff', label: 'Backoff', detail: '800 ms remaining', kind: 'failure' }, { id: 'failed', label: 'Failed', detail: 'manual action', kind: 'failure' },
        ]
      : [
          { id: 'idle', label: 'Idle', detail: 'no socket', kind: 'initial' }, { id: 'dialing', label: 'Dialing', detail: '500 ms budget' }, { id: 'ready', label: 'Ready', detail: 'requests allowed', kind: 'success' },
          { id: 'backoff', label: 'Backoff', detail: '800 ms remaining', kind: 'failure' }, { id: 'failed', label: 'Failed', detail: 'manual action', kind: 'failure' },
        ],
    transitions: stress
      ? [
          { id: 'connect', from: 'idle', to: 'dialing', event: 'connect' }, { id: 'socket-open', from: 'dialing', to: 'auth', event: 'socket opens' }, { id: 'authenticated', from: 'auth', to: 'ready', event: 'token accepted' },
          { id: 'timeout', from: 'dialing', to: 'backoff', event: 'timeout', kind: 'failure', guard: '500 ms elapsed' }, { id: 'retry', from: 'backoff', to: 'dialing', event: 'retry timer', kind: 'retry', guard: '800 ms elapsed' }, { id: 'reject', from: 'auth', to: 'failed', event: 'token rejected', kind: 'failure' },
        ]
      : [
          { id: 'connect', from: 'idle', to: 'dialing', event: 'connect' }, { id: 'connected', from: 'dialing', to: 'ready', event: 'socket opens' },
          { id: 'timeout', from: 'dialing', to: 'backoff', event: 'timeout', kind: 'failure', guard: '500 ms elapsed' }, { id: 'retry', from: 'backoff', to: 'dialing', event: 'retry timer', kind: 'retry', guard: '800 ms elapsed' }, { id: 'exhausted', from: 'backoff', to: 'failed', event: 'retry exhausted', kind: 'failure' },
        ],
    focus,
    blockedAction: { stateId: 'backoff', event: 'send request', reason: 'the socket is not Ready' },
    provenance: 'Connection recovery contract · explicit timeout and backoff budgets, illustrative transport values',
  };
}
